import { useEffect, useState } from 'react';
import { Box, Chip, Typography } from '@mui/material';
import { useSearchParams } from 'react-router-dom';
import PageWrapper from 'components/Common/PageWraper';
import CustomizedTable from '../../CustomizedTable/CustomizedTable';
import { useDataReconciliation } from './hooks/useDataReconciliation';

const getStyledColumns = (columns: any[]) => columns.map((column) => ({
  ...column,
  formatter: ({ row }: any) => {
    const formattedValue = column.formatter({ row });
    const normalizedValue = String(formattedValue).trim().toUpperCase();
    const bgColor = normalizedValue === 'MATCH' ? '#01C008' : '#FE2626';

    if (column.key.includes('_vs_') && normalizedValue === 'N/A') {
      return '--';
    }

    if (normalizedValue === 'N/A') {
      return <Typography sx={{ color: '#A9ADCB', fontWeight: 500, fontSize: '14px' }}>N/A</Typography>;
    }

    if (column.key.includes('_vs_') && (normalizedValue === 'MATCH' || normalizedValue === 'MISMATCH')) {
      return (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%', height: '100%' }}>
          <Chip
            label={normalizedValue}
            size="small"
            sx={{
              width: 'fit-content',
              maxWidth: 'none',
              flexShrink: 0,
              height: 18,
              borderRadius: '999px',
              bgcolor: `${bgColor} !important`,
              color: '#fff !important',
              fontSize: 10,
              fontWeight: 600,
              marginBottom: '0px !important',
              minHeight: '24px !important',
              minWidth: '110px !important',
              '& .MuiChip-label': {
                px: 1,
                whiteSpace: 'nowrap',
                overflow: 'visible',
                textOverflow: 'clip',
              },
            }}
          />
        </Box>
      );
    }

    return formattedValue;
  },
}));

const DataReconciliationDashboard = () => {
  const [searchParams] = useSearchParams();
  const activeProgram = searchParams.get('reconTab') === 'master' ? 'Master' : 'Transactional';
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const tableTitles = {
    Master: 'Consolidated Master Data Recon Status',
    Transactional: 'Consolidated Transactional Data Recon Status',
  } as const;
  const { tableViews, handleExport, dateRangePayload } = useDataReconciliation(activeProgram);
  const activeTable = tableViews.find(({ program }) => program === activeProgram);

  useEffect(() => {
    setPage(1);
  }, [activeProgram, dateRangePayload]);

  return (
    <PageWrapper sx={{ minHeight: 'calc(100vh - 120px)' }}>
      <Box sx={{ display: 'grid', gap: 3, width: '100%' }}>
        {activeTable && (
          <Box key={activeTable.program} sx={{ width: '100%', minWidth: 0, maxWidth: '100%' }}>
            <CustomizedTable
              tableTitle={tableTitles[activeTable.program]}
              tableData={activeTable.rows.slice((page - 1) * pageSize, page * pageSize)}
              total={activeTable.total || activeTable.rows.length}
              page={page}
              pageSize={pageSize}
              onPageChange={setPage}
              onPageSizeChange={(size) => {
                setPageSize(size);
                setPage(1);
              }}
              onSorting={activeTable.onSorting}
              sortingInfo={activeTable.sortingInfo}
              defaultColumns={activeTable.programColumns.map((column) => column.name)}
              tableType={`data-reconciliation-${activeTable.program.toLowerCase().replace(/\s/g, '-')}`}
              columns={getStyledColumns(activeTable.programColumns)}
              hasGroupBy={false}
              isFetching={activeTable.isLoading}
              showExport={true}
              isSortIcon={true}
              isPrevNextPagination={true}
              fileName={`${activeTable.program.replace(/\s+/g, '_')}_Data_Reconciliation.xlsx`}
              exportTableData={(preferredColumnNames: string[]) => handleExport(activeTable.program, activeTable.programColumns, preferredColumnNames)}
            />
          </Box>
        )}
      </Box>
    </PageWrapper>
  );
};

export default DataReconciliationDashboard;